import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus, Music } from 'lucide-react'
import { playlists } from '../api/client'
import type { Track, TrackWithDetails, Playlist } from '../types'

interface AddToPlaylistModalProps {
  track: Track | TrackWithDetails
  isOpen: boolean
  onClose: () => void
}

export default function AddToPlaylistModal({ track, isOpen, onClose }: AddToPlaylistModalProps) {
  const [newPlaylistName, setNewPlaylistName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const queryClient = useQueryClient()

  const { data: userPlaylists, isLoading } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => playlists.list(),
    enabled: isOpen,
  })

  const addToPlaylistMutation = useMutation({
    mutationFn: (playlistId: string) => playlists.addTrack(playlistId, track.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playlists'] })
      onClose()
    },
  })

  const createPlaylistMutation = useMutation({
    mutationFn: (name: string) => playlists.create(name),
    onSuccess: async (newPlaylist) => {
      await playlists.addTrack(newPlaylist.id, track.id)
      queryClient.invalidateQueries({ queryKey: ['playlists'] })
      setNewPlaylistName('')
      setIsCreating(false)
      onClose()
    },
  })

  const handleAddToPlaylist = (playlistId: string) => {
    addToPlaylistMutation.mutate(playlistId)
  }

  const handleCreateAndAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPlaylistName.trim()) {
      createPlaylistMutation.mutate(newPlaylistName.trim())
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-dark-800 rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <h2 className="text-lg font-semibold">Add to Playlist</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-dark-700 text-dark-400 hover:text-dark-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {/* Track info */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-dark-700">
            <div className="w-12 h-12 bg-dark-700 rounded flex items-center justify-center">
              <Music size={20} className="text-dark-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{track.title}</p>
              <p className="text-sm text-dark-400 truncate">
                {'artist_name' in track ? track.artist_name : 'Unknown Artist'}
              </p>
            </div>
          </div>

          {/* Create new playlist */}
          {isCreating ? (
            <form onSubmit={handleCreateAndAdd} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder="Playlist name"
                  className="flex-1 bg-dark-700 border border-dark-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!newPlaylistName.trim() || createPlaylistMutation.isPending}
                  className="btn btn-primary text-sm"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full flex items-center gap-3 p-3 rounded hover:bg-dark-700 text-dark-200 mb-4"
            >
              <div className="w-10 h-10 bg-dark-600 rounded flex items-center justify-center">
                <Plus size={20} />
              </div>
              <span>Create new playlist</span>
            </button>
          )}

          {/* Playlist list */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <p className="text-dark-400 text-center py-4">Loading playlists...</p>
            ) : userPlaylists && userPlaylists.length > 0 ? (
              <div className="space-y-1">
                {userPlaylists.map((playlist: Playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => handleAddToPlaylist(playlist.id)}
                    disabled={addToPlaylistMutation.isPending}
                    className="w-full flex items-center gap-3 p-3 rounded hover:bg-dark-700 text-left"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 rounded flex items-center justify-center">
                      <Music size={16} />
                    </div>
                    <span className="flex-1 truncate">{playlist.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-dark-400 text-center py-4">No playlists yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
